import { Drawer } from '@framework/ui/components/drawer/Drawer.tsx';
import { message } from 'antd';
import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type AnyObject, type ObjectSchema } from 'yup';
import { type CreateCommunicationRequest } from '../../schema/communication/CreateCommunicationRequestSchema.ts';
import { CommunicationForm } from './form/CommunicationForm.tsx';

export interface ICommunicationButtonDrawerProps<
  T extends AnyObject,
  TSchema extends ObjectSchema<T> = ObjectSchema<T>,
> {
  /** Comunicazione selezionata */
  communication?: CreateCommunicationRequest | null;
  validationSchema: TSchema;
  onClose?: () => void;
}

/**
 * Drawer creazione e invio di una nuova comunicazione
 */
export function CommunicationButtonDrawer<
  T extends AnyObject,
  TSchema extends ObjectSchema<T>,
>(props: ICommunicationButtonDrawerProps<T, TSchema>) {
  const { communication, validationSchema, onClose } = props;
  const { t } = useTranslation();

  const open = communication != null;

  const handleClose = useCallback(() => {
    onClose?.();
  }, [onClose]);

  useEffect(() => {
    message.config({ top: open ? 0 : 70 });
  }, [open]);

  const drawerTitle = useMemo(() => {
    if (communication?.idComunicazione) {
      return t('communication-button.edit-comm', 'Modifica Comunicazione');
    }
    if (communication?.isManual) {
      return t('communication-button.load-comm', 'Carica Comunicazione');
    }
    return t('communication-button.new-comm', 'Nuova Comunicazione');
  }, [communication?.idComunicazione, communication?.isManual, t]);

  return (
    <Drawer
      open={open}
      onClose={handleClose}
      destroyOnClose
      maskClosable={false}
      title={drawerTitle}
      size="large"
      placement="right"
    >
      <CommunicationForm
        communication={communication}
        validationSchema={validationSchema as any}
        onClose={handleClose}
      />
    </Drawer>
  );
}
